import { AvatarSizes, useAppTheme, useIntl, WithSkeleton } from '@capsicum/common';
import { CondensedButton, Image, Label, ListCard, SkeletonItem, Timer, useRouter } from '@capsicum/common-web';
import { CheckIcon } from '@capsicum/common-web/icons';
import { useWCPledgeTokenFormats, type WCPledgeTeam } from '@capsicum/features/world-championship-pledge/shared';
import React from 'react';
import styled, { useTheme } from 'styled-components';

export type PartneredCountryCardProps = {
	country: WCPledgeTeam;
	hasPrice?: boolean;
};

export const WCPledgePartneredTeamCardComponent = ({ country }: PartneredCountryCardProps) => {
	const { $t } = useIntl();
	const router = useRouter();
	const handleClick = () => {
		router.push('/locker-room-internationals/basket');
	};
	const { theme } = useAppTheme();
	const { colors } = useTheme();
	const { hasStakingPeriodEnded, tokenAltPrice, tokenAmount, tokenPrice } = useWCPledgeTokenFormats(country);

	return (
		<S.PartneredCountryCard
			$userCanPledge={country.canUserPledge}
			data-testid={`wcpledge-partnered-card-${country.name}`}
			key={country.name}
			onClick={country.canUserPledge ? handleClick : undefined}
		>
			{!hasStakingPeriodEnded && country?.endDate && (
				<ListCard.Ribbon backgroundColor={theme.colors.backgrounds.info.subtle}>
					<>
						<S.AvailableIn color="main.enabled">
							{$t('worldChampionshipPledge.tokenTab.availableIn')}
						</S.AvailableIn>
						<Timer
							customFormat="digitalTimer"
							expirationDate={country?.endDate}
							size="xsmall"
							testID={`wcpledge-partnered-card-${country.name}`}
							type="default"
						/>
					</>
				</ListCard.Ribbon>
			)}
			<S.Container>
				<S.StartContainer>
					<Image
						alt={`${country.name} flag`}
						height={AvatarSizes.S}
						src={country.flagUrl}
						width={AvatarSizes.S}
					/>
				</S.StartContainer>
				<S.CardBody>
					<S.JoinedWrapper>
						{$t('worldChampionshipPledge.tokenTab.statusJoined')}
						<CheckIcon color={colors.borders.success.enabled} height={18} width={18} />
					</S.JoinedWrapper>
					<ListCard.Row>
						<ListCard.Title>{country.name}</ListCard.Title>
					</ListCard.Row>
					<S.TokenTextsWrapper>
						<S.TokenAmount>{tokenAmount}</S.TokenAmount>
						<S.TokenPrice>{tokenPrice}</S.TokenPrice>
						<S.TokenConversion>{tokenAltPrice}</S.TokenConversion>
					</S.TokenTextsWrapper>
				</S.CardBody>
				<S.EndContainer>
					{!hasStakingPeriodEnded ? (
						<CondensedButton
							color="brand"
							size="small"
							testID={`wcpledge-tokens-tab-partnered-${country.name}-unstakingCHZ-label`}
							text={$t('worldChampionshipPledge.tokenTab.stakeMoreJoinedTeam')}
							textSize="small"
							onClick={country.canUserPledge ? handleClick : undefined}
						/>
					) : (
						<Label
							label={$t('worldChampionshipPledge.tokenTab.stakingPeriodEnded')}
							size="condensed"
							testID={`wcpledge-tokens-tab-partnered-${country.name}-unstakingCHZ-label`}
							variant="grey"
						/>
					)}
				</S.EndContainer>
			</S.Container>
		</S.PartneredCountryCard>
	);
};

const WCPledgePartneredTeamCardSkeleton = () => (
	<S.PartneredCountryCard $userCanPledge>
		<S.Container>
			<S.StartContainer>
				<ListCard.Image isLoading />
			</S.StartContainer>
			<S.CardBody>
				<ListCard.Row>
					<SkeletonItem borderRadius={2} height={20} width={108} />
				</ListCard.Row>
			</S.CardBody>
			<S.EndContainer>
				<SkeletonItem borderRadius={2} height={20} width={20} />
			</S.EndContainer>
		</S.Container>
	</S.PartneredCountryCard>
);

export const WCPledgePartneredTeamCard = React.memo(
	WithSkeleton(WCPledgePartneredTeamCardComponent, WCPledgePartneredTeamCardSkeleton),
	(prev, next) => prev.isLoading === next.isLoading && prev?.country?.id === next?.country?.id
);

namespace S {
	export const PartneredCountryCard = styled(ListCard)<{ $userCanPledge: boolean }>`
		display: flex;
		cursor: pointer;
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
		opacity: ${({ $userCanPledge }) => ($userCanPledge ? 1 : 0.5)};
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: row;
		width: 100%;
		height: 100%;
		padding: ${({ theme }) => `${theme.spacings.small} ${theme.spacings.xxsmall}`};
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const StartContainer = styled(ListCard.Start)`
		align-items: flex-start;
		align-items: center;
		padding: 0;
	`;

	export const CardBody = styled(ListCard.Content)`
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxxsmall};
	`;

	export const Subtitle = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: baseline;
		color: ${({ theme }) => theme.colors.text.main.subtle};
		width: 100%;
		${({ theme }) => theme.fonts.text.text5}
	`;

	export const EndContainer = styled(ListCard.End)`
		align-items: flex-end;
		align-items: center;
	`;
	export const AvailableIn = styled.div`
		margin-right: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const TokenTextsWrapper = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: baseline;
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const JoinedWrapper = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: baseline;
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const TokenAmount = styled.div`
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const TokenPrice = styled.div`
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const TokenConversion = styled.div`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		${({ theme }) => theme.fonts.text.text4}
	`;
}
